import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Importi in formato italiano (35,00), come nel resto del gestionale.
const euroIt = (v: number): string =>
    (Number(v) || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });


const NAVY: [number, number, number]    = [30, 46, 69];
const BORDEAUX: [number, number, number] = [139, 26, 26];
const SLATE: [number, number, number]   = [100, 116, 139];
const BORDER: [number, number, number]  = [226, 232, 240];
const BG_SOFT: [number, number, number] = [248, 250, 252];
const BG_ALT: [number, number, number]  = [249, 250, 251];
const BG_TOT: [number, number, number]  = [241, 245, 249];

function fmtDate(iso?: string | null): string {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}
function fmtTime(iso?: string | null): string {
    if (!iso) return '-';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}
function fmtNum(n?: number | null): string {
    if (n == null) return '-';
    return n.toFixed(1);
}
// Ore in formato "Xh Ym" arrotondate al minuto: con i blocchi da 10 minuti
// i valori sotto i 5 minuti diventavano "0" anche con tracciato presente.
function fmtHM(h?: number | null): string {
    if (h == null || h <= 0) return '0m';
    const totalMin = Math.max(1, Math.round(h * 60));
    const hh = Math.floor(totalMin / 60);
    const mm = totalMin % 60;
    if (hh === 0) return `${mm}m`;
    if (mm === 0) return `${hh}h`;
    return `${hh}h ${mm}m`;
}

// Ore approvate di una relazione, con la stessa logica delle modali utenti/collaboratori:
//   - oreApprovateTotali (override del titolare) se valorizzato;
//   - altrimenti oreGps + oreExtraApprovate (o oreExtra se il campo approvato manca).
// Per le relazioni non APPROVATO ritorna null.
function oreApprovateRelazione(r: any): number | null {
    if (!r || r.stato !== 'APPROVATO') return null;
    if (r.oreApprovateTotali != null) return Math.max(0, Number(r.oreApprovateTotali));
    const extra = r.oreExtraApprovate != null ? Number(r.oreExtraApprovate) : Number(r.oreExtra || 0);
    return Math.max(0, Number(r.oreGps || 0) + Math.max(0, extra));
}

// Km approvati (modificabili dal titolare in approvazione), altrimenti kmPercorsi.
function kmApprovatiRelazione(r: any): number {
    if (!r) return 0;
    return Math.max(0, Number(r.kmApprovati ?? r.kmPercorsi ?? 0));
}

function drawHeader(doc: jsPDF, W: number, nome: string, from: string, to: string) {
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, W, 26, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('AGENDA COLLABORATORE', 14, 10);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(nome || '-', 14, 17);
    doc.setFontSize(8);
    doc.text(`Periodo: ${fmtDate(from)} – ${fmtDate(to)}`, 14, 23);
    doc.text(`Generato il ${new Date().toLocaleDateString('it-IT')}`, W - 14, 23, { align: 'right' });
}

function drawSectionTitle(doc: jsPDF, y: number, label: string, color: [number, number, number]) {
    // Barra laterale + titolo, più sobrio di un titolo a colore pieno
    doc.setFillColor(...color);
    doc.rect(14, y - 4, 2.5, 6, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...NAVY);
    doc.text(label.toUpperCase(), 19, y);
    doc.setTextColor(0, 0, 0);
}

function drawKpis(doc: jsPDF, W: number, y: number, kpis: { label: string; value: string }[]) {
    const kpiW = (W - 28) / kpis.length;
    kpis.forEach((k, i) => {
        const x = 14 + i * kpiW;
        doc.setFillColor(...BG_SOFT);
        doc.setDrawColor(...BORDER);
        doc.roundedRect(x, y, kpiW - 2, 16, 2, 2, 'FD');
        doc.setFontSize(7);
        doc.setTextColor(...SLATE);
        doc.setFont('helvetica', 'normal');
        doc.text(k.label.toUpperCase(), x + (kpiW - 2) / 2, y + 5, { align: 'center' });
        doc.setFontSize(12);
        doc.setTextColor(...NAVY);
        doc.setFont('helvetica', 'bold');
        doc.text(k.value, x + (kpiW - 2) / 2, y + 12, { align: 'center' });
    });
}

export function generaAgendaCollaboratore(agenda: any, from: string, to: string) {
    const { collaboratore, incarichi, turni } = agenda;
    const nome = `${collaboratore?.name || ''} ${collaboratore?.surname || ''}`.trim();
    const tuttiGli: any[] = incarichi || [];
    const tuttiTurni: any[] = turni || [];

    // ── Aggregati per KPI ──
    // Km auto divisi tra propria e aziendale; le spese "senza carta aziendale" sono
    // la somma degli importi con anticipatoAzienda=false.
    let totOreInv = 0;
    let totKmPropria = 0;
    let totKmAziendale = 0;
    let totSpeseTasca = 0;
    let totRelazioni = 0;
    // Flag auto aziendale per singola relazione (può cambiare giorno per giorno);
    // se la relazione non lo valorizza si usa quello dell'incarico.
    const isAutoRel = (inc: any, r: any): boolean => {
        if (r && r.autoAziendale != null) return !!r.autoAziendale;
        return !!inc?.autoAziendale;
    };
    for (const inc of tuttiGli) {
        for (const r of (inc.relazioni || [])) {
            totRelazioni++;
            // I totali contano solo ore e km approvati: relazioni in BOZZA / DA_REVISIONARE /
            // DA_MODIFICARE e sessioni senza relazione restano fuori.
            const oreOk = oreApprovateRelazione(r);
            if (oreOk != null) {
                totOreInv += oreOk;
                const km = kmApprovatiRelazione(r);
                if (isAutoRel(inc, r)) totKmAziendale += km; else totKmPropria += km;
            }
            for (const sp of (r.spese || [])) {
                if (sp.anticipatoAzienda) continue;
                totSpeseTasca += sp.importoApprovato ?? sp.importo ?? 0;
            }
        }
    }
    const totKmInv = totKmPropria + totKmAziendale;
    // Ore pagate di un turno per KPI e totale:
    //   - solo turni con ore approvate, così il riepilogo riflette quanto
    //     effettivamente riconosciuto al collaboratore;
    //   - override del titolare se valorizzato;
    //   - altrimenti GPS (effettivoFine - effettivoInizio) + ore extra del report
    //     fine turno, che in approvazione vengono copiate in oreExtraApprovate.
    const oreTurno = (t: any): number => {
        if (!t.oreApprovate) return 0;
        if (t.oreCollaboratoreOverride != null) {
            return Math.max(0, Number(t.oreCollaboratoreOverride));
        }
        let ore = 0;
        if (t.effettivoInizio && t.effettivoFine) {
            ore += Math.max(0, (new Date(t.effettivoFine).getTime() - new Date(t.effettivoInizio).getTime()) / 3_600_000);
        }
        ore += Math.max(0, Number(t.report?.oreExtra || 0));
        return ore;
    };
    let totOreTurni = 0;
    let totKmTurni = 0;
    // Km dei servizi: km approvati nel report fine turno (o kmPercorsi). Come per
    // le ore, contano solo i turni approvati.
    for (const t of tuttiTurni) {
        totOreTurni += oreTurno(t);
        if (t.oreApprovate) {
            const km = t.report?.kmApprovati ?? t.report?.kmPercorsi ?? 0;
            totKmTurni += Math.max(0, Number(km));
        }
    }
    const totKmGlobale = totKmInv + totKmTurni;

    // ── Orientamento orizzontale come gli altri riepiloghi ──
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();

    drawHeader(doc, W, nome, from, to);

    // ── KPI bar ──
    const kpiY = 32;
    drawKpis(doc, W, kpiY, [
        { label: 'Incarichi',     value: `${tuttiGli.length}` },
        { label: 'Relazioni',     value: `${totRelazioni}` },
        // Ore e km approvati, come nei box della modale Agenda Collaboratore.
        { label: 'Ore appr.',     value: fmtHM(totOreInv) },
        { label: 'Km approvati',  value: `${Math.round(totKmGlobale)}` },
        { label: 'Turni',         value: `${tuttiTurni.length}` },
        { label: 'Ore Turni',     value: fmtHM(totOreTurni) },
    ]);

    let y = kpiY + 22;

    // ── Sezione Investigazioni ──
    if (tuttiGli.length > 0) {
        drawSectionTitle(doc, y, 'Attività Investigative', BORDEAUX);
        y += 4;

        // Colonne: Data, Mandato (con " – Soggetto"), Ore registrate, Ore extra, Ore appr.,
        // Km auto propria, Km auto aziendale, Spese tasca.
        // "Ore appr." è quanto liquidato dopo l'approvazione; "-" se non ancora approvata.
        // `praticaNome` può già contenere "Mandato - Cliente": si taglia da " - " (o em-dash)
        // per non ripetere il cliente e si aggiunge il soggetto.
        const stripClient = (s: string) => s.split(/\s+[-–]\s+/)[0].trim();
        const rows: any[] = [];
        for (const inc of tuttiGli) {
            const dataInizio = fmtDate(inc.dataInizio);
            const praticaBase = stripClient(inc.praticaNome || `#${inc.incaricoId || inc.id}`);
            const soggetto = inc.soggettoNome || '';
            const mandato = soggetto ? `${praticaBase} - ${soggetto}` : praticaBase;

            if (inc.relazioni?.length > 0) {
                for (const r of inc.relazioni) {
                    const dataRel = fmtDate(r.dataRelazione);
                    const oreReg = Number(r.oreGps || 0);
                    const oreExtra = Number(r.oreExtra || 0);
                    const oreOk = oreApprovateRelazione(r);
                    // Km approvati se la relazione è approvata (possono differire dai dichiarati),
                    // altrimenti quelli richiesti, solo informativi.
                    const km = oreOk != null ? kmApprovatiRelazione(r) : Number(r.kmPercorsi || 0);
                    // L'auto aziendale va letta per giornata (r.autoAziendale), non sul mandato.
                    const isAuto = isAutoRel(inc, r);
                    const kmPropria = isAuto ? 0 : km;
                    const kmAziendale = isAuto ? km : 0;
                    const speseTasca = (r.spese || [])
                        .filter((sp: any) => !sp.anticipatoAzienda)
                        .reduce((acc: number, sp: any) => acc + (sp.importoApprovato ?? sp.importo ?? 0), 0);
                    rows.push([
                        dataRel,
                        mandato,
                        fmtHM(oreReg),
                        fmtHM(oreExtra),
                        oreOk != null ? fmtHM(oreOk) : '-',
                        kmPropria > 0 ? `${Math.round(kmPropria)}` : '-',
                        kmAziendale > 0 ? `${Math.round(kmAziendale)}` : '-',
                        speseTasca > 0 ? `€ ${euroIt(speseTasca)}` : '-',
                    ]);
                }
            } else {
                // Incarico senza relazioni: solo ore GPS dalle sessioni, nessun valore approvato.
                const oreSessioni = (inc.sessioni || []).reduce((acc: number, s: any) => {
                    if (!s.startTime || !s.endTime) return acc;
                    return acc + Math.max(0, (new Date(s.endTime).getTime() - new Date(s.startTime).getTime()) / 3_600_000);
                }, 0);
                rows.push([
                    dataInizio,
                    mandato,
                    oreSessioni > 0 ? fmtHM(oreSessioni) : '-',
                    '-',
                    '-',
                    '-', '-', '-',
                ]);
            }
        }

        // Riga totali: 2 celle merge sull'etichetta + 3 colonne ore + 2 km + 1 spese.
        rows.push([
            { content: 'TOTALI', colSpan: 2, styles: { fontStyle: 'bold', fillColor: BG_TOT, textColor: NAVY } },
            { content: '', styles: { fillColor: BG_TOT } },
            { content: '', styles: { fillColor: BG_TOT } },
            { content: fmtHM(totOreInv), styles: { fontStyle: 'bold', halign: 'center', fillColor: BG_TOT, textColor: NAVY } },
            { content: totKmPropria > 0 ? `${Math.round(totKmPropria)}` : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: BG_TOT, textColor: NAVY } },
            { content: totKmAziendale > 0 ? `${Math.round(totKmAziendale)}` : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: BG_TOT, textColor: NAVY } },
            { content: totSpeseTasca > 0 ? `€ ${euroIt(totSpeseTasca)}` : '€ 0,00', styles: { fontStyle: 'bold', halign: 'center', fillColor: BG_TOT, textColor: NAVY } },
        ] as any);

        autoTable(doc, {
            startY: y + 2,
            head: [['Data', 'Mandato', 'Ore reg.', 'Ore extra', 'Ore appr.', 'Km auto propria', 'Km auto aziendale', 'Spese senza carta az.']],
            body: rows,
            theme: 'grid',
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
            headStyles: { fillColor: BORDEAUX, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            columnStyles: {
                0: { cellWidth: 22, halign: 'center' },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 18, halign: 'center' },
                3: { cellWidth: 18, halign: 'center' },
                4: { cellWidth: 18, halign: 'center' },
                5: { cellWidth: 26, halign: 'center' },
                6: { cellWidth: 28, halign: 'center' },
                7: { cellWidth: 32, halign: 'center' },
            },
            alternateRowStyles: { fillColor: BG_ALT },
            margin: { left: 14, right: 14 },
        });
        y = (doc as any).lastAutoTable.finalY + 10;
    }

    // ── Sezione Turni Servizio ── (sempre presente, anche vuota, a conferma
    // che è stata elaborata).
    if (y > H - 50) { doc.addPage(); y = 20; }
    drawSectionTitle(doc, y, 'Turni Servizio', NAVY);
    y += 4;

    if (tuttiTurni.length > 0) {
        // Colonne: Data, Servizio, Indirizzo, Pianificato, Entrata, Uscita, Ore Approvate, Km.
        // Ore approvate = quanto riconosciuto (0 se non approvato); Km = km approvati o kmPercorsi.
        const rows: any[] = tuttiTurni.map((t: any) => {
            const data = fmtDate(t.dataTurno);
            const servizio = t.servizio?.nome || '-';
            const indirizzo = t.servizio?.indirizzo || '-';
            const pianificato = `${t.oraInizio || '?'} - ${t.oraFine || '?'}`;
            const entrata = t.effettivoInizio ? fmtTime(t.effettivoInizio) : '-';
            const uscita = t.effettivoFine ? fmtTime(t.effettivoFine) : '-';
            const ore = oreTurno(t);
            const oreCell = t.oreApprovate ? fmtHM(ore) : '-';
            const km = t.oreApprovate
                ? Number(t.report?.kmApprovati ?? t.report?.kmPercorsi ?? 0)
                : Number(t.report?.kmPercorsi || 0);
            const kmCell = km > 0 ? `${Math.round(km)}` : '-';
            return [data, servizio, indirizzo, pianificato, entrata, uscita, oreCell, kmCell];
        });

        rows.push([
            { content: 'TOTALI', colSpan: 3, styles: { fontStyle: 'bold', fillColor: BG_TOT, textColor: NAVY } },
            { content: `${tuttiTurni.length} turni`, styles: { fontStyle: 'bold', halign: 'center', fillColor: BG_TOT, textColor: NAVY } },
            { content: '', colSpan: 2, styles: { fillColor: BG_TOT } },
            { content: fmtHM(totOreTurni), styles: { fontStyle: 'bold', halign: 'center', fillColor: BG_TOT, textColor: NAVY } },
            { content: totKmTurni > 0 ? `${Math.round(totKmTurni)}` : '0', styles: { fontStyle: 'bold', halign: 'center', fillColor: BG_TOT, textColor: NAVY } },
        ] as any);

        autoTable(doc, {
            startY: y + 2,
            head: [['Data', 'Servizio', 'Indirizzo', 'Pianificato', 'Entrata', 'Uscita', 'Ore Approvate', 'Km']],
            body: rows,
            theme: 'grid',
            styles: { fontSize: 7.5, cellPadding: 2.5, overflow: 'linebreak' },
            headStyles: { fillColor: NAVY, textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
            columnStyles: {
                0: { cellWidth: 22, halign: 'center' },
                1: { cellWidth: 50 },
                2: { cellWidth: 'auto' },
                3: { cellWidth: 26, halign: 'center' },
                4: { cellWidth: 18, halign: 'center' },
                5: { cellWidth: 18, halign: 'center' },
                6: { cellWidth: 24, halign: 'center' },
                7: { cellWidth: 16, halign: 'center' },
            },
            alternateRowStyles: { fillColor: BG_ALT },
            margin: { left: 14, right: 14 },
        });
    } else {
        // Placeholder se non ci sono turni, per non dare l'impressione che la sezione manchi.
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(...SLATE);
        doc.text('Nessun turno nel periodo.', 14, y + 8);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
    }

    // ── Footer ──
    const pageCount = (doc as any).internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(...BORDER);
        doc.setLineWidth(0.2);
        doc.line(14, H - 10, W - 14, H - 10);
        doc.setFontSize(7);
        doc.setTextColor(150);
        doc.text('Documento ad uso interno — non riporta compensi né tariffe', 14, H - 5);
        doc.text(`Pag. ${i} / ${pageCount}`, W - 14, H - 5, { align: 'right' });
    }

    const safeName = (nome || 'Collaboratore').replace(/\s+/g, '_');
    doc.save(`Agenda_${safeName}_${from}_${to}.pdf`);
}
